import React, { useEffect, useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Eye, Info } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 07/2026: Backend liefert UTC-Zeitstempel teils ohne Zeitzonen-Suffix →
// explizit als UTC parsen und in Schweizer Zeit anzeigen (Fix "21:00 statt 23:00")
const fmtSwissDateTime = (s) => {
  if (!s) return '-';
  const d = new Date(/Z|[+-]\d{2}:\d{2}$/.test(s) ? s : `${s}Z`);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleString('de-CH', { timeZone: 'Europe/Zurich' });
};

const PaymentsPage = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [serverPagination, setServerPagination] = useState(false);
  const [serverTotal, setServerTotal] = useState(0);
  const [sortField, setSortField] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;
  const [statusFilter, setStatusFilter] = useState('all');
  const [methodFilter, setMethodFilter] = useState('all');
  const [customerFilter, setCustomerFilter] = useState('');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (methodFilter !== 'all') params.payment_method = methodFilter;
      if (customerFilter.trim()) params.customer_id = customerFilter.trim();

      const res = await axios.get(`${API}/payments`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setPayments(res.data);
      setCurrentPage(1);
    } catch (e) {
      console.error(e);
      toast.error('Fehler beim Laden der Zahlungen');
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentsServer = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const params = {
        page: currentPage,
        limit: pageSize,
        sort_field: sortField,
        sort_dir: sortDir
      };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (methodFilter !== 'all') params.payment_method = methodFilter;
      if (customerFilter.trim()) params.customer_id = customerFilter.trim();

      const res = await axios.get(`${API}/payments/paginated`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setPayments(res.data.items || []);
      setServerTotal(res.data.total || 0);
    } catch (e) {
      console.error(e);
      toast.error('Fehler beim Laden der Zahlungen');
    } finally {
      setLoading(false);
    }
  };

  const refreshPayments = () => {
    if (serverPagination) {
      fetchPaymentsServer();
    } else {
      fetchPayments();
    }
  };

  useEffect(() => {
    if (!serverPagination) {
      refreshPayments();
    }
    // eslint-disable-next-line
  }, [statusFilter, methodFilter, serverPagination, sortField, sortDir]);

  useEffect(() => {
    if (serverPagination) {
      fetchPaymentsServer();
    }
  }, [serverPagination, statusFilter, methodFilter, customerFilter, currentPage, sortField, sortDir]);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, methodFilter, customerFilter, serverPagination, sortField, sortDir]);

  const totalItems = serverPagination ? serverTotal : payments.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  const displayPayments = useMemo(() => {
    if (serverPagination) return payments;

    const sorted = [...payments].sort((a, b) => {
      const dir = sortDir === 'desc' ? -1 : 1;
      if (sortField === 'amount') {
        return dir * ((a.amount || 0) - (b.amount || 0));
      }
      if (sortField === 'status') {
        return dir * (a.status || '').localeCompare(b.status || '');
      }
      if (sortField === 'customer') {
        const aValue = a.customer_name || a.customer_id || '';
        const bValue = b.customer_name || b.customer_id || '';
        return dir * aValue.localeCompare(bValue);
      }
      const aDate = new Date(a.created_at || 0).getTime();
      const bDate = new Date(b.created_at || 0).getTime();
      return dir * (aDate - bDate);
    });

    return sorted.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  }, [serverPagination, payments, sortField, sortDir, currentPage, pageSize]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleRefund = async (paymentId, method) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/payments/${paymentId}/refund`,
        null,
        {
          params: { refund_method: method },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success('Zahlung wurde als rückerstattet markiert');
      refreshPayments();
    } catch (e) {
      console.error(e);
      toast.error('Fehler bei der Rückerstattung');
    }
  };

  const renderStatusBadge = (status) => {
    const map = {
      pending: { label: 'Offen', color: 'bg-yellow-100 text-yellow-800' },
      open: { label: 'Offen', color: 'bg-yellow-100 text-yellow-800' },
      completed: { label: 'Bezahlt', color: 'bg-green-100 text-green-800' },
      failed: { label: 'Fehlgeschlagen', color: 'bg-red-100 text-red-800' },
      refunded: { label: 'Rückerstattet', color: 'bg-blue-100 text-blue-800' },
      cancelled: { label: 'Storniert', color: 'bg-gray-100 text-gray-800' },
    };
    const cfg = map[status] || map.pending;
    return <Badge className={cfg.color}>{cfg.label}</Badge>;
  };

  const handleViewDetails = (payment) => {
    setSelectedPayment(payment);
    setDetailDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="payments-page">
        <div>
          <h1 className="text-3xl font-bold">Zahlungen</h1>
          <p className="text-gray-600 mt-1">Verwalten Sie Zahlungen und Rechnungen</p>
        </div>
        <Card>
          <CardContent className="p-8 text-center text-gray-500">Lädt...</CardContent>
        </Card>
      </div>
    );
  }

  const paymentsContent = (
    <>
      <Card>
      <CardHeader>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle>Alle Zahlungen ({totalItems})</CardTitle>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <div className="flex-1 sm:flex-none">
              <Input
                placeholder="Kunden-ID filtern..."
                value={customerFilter}
                onChange={(e) => setCustomerFilter(e.target.value)}
                onBlur={() => {
                  if (!serverPagination) {
                    refreshPayments();
                  }
                }}
                className="w-full sm:w-56"
                data-testid="payments-customer-filter-input"
              />
            </div>
            <div className="flex items-center gap-2" data-testid="payments-server-pagination-toggle">
              <Switch
                checked={serverPagination}
                onCheckedChange={setServerPagination}
                data-testid="payments-server-pagination-switch"
              />
              <span className="text-sm text-gray-600">Server Pagination</span>
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button type="button" aria-label="Server Pagination Info" data-testid="payments-server-pagination-info">
                      <Info className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-left">
                    Lädt Daten schrittweise vom Server, um die Performance bei vielen Einträgen zu verbessern. Aus: Alle Daten werden auf einmal geladen (für kleine Listen).
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Select value={sortField} onValueChange={setSortField}>
              <SelectTrigger className="w-full sm:w-40" data-testid="payments-sort-field-select">
                <SelectValue placeholder="Sortierung" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date">Datum</SelectItem>
                <SelectItem value="amount">Betrag</SelectItem>
                <SelectItem value="status">Status</SelectItem>
                <SelectItem value="customer">Kunde</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sortDir} onValueChange={setSortDir}>
              <SelectTrigger className="w-full sm:w-40" data-testid="payments-sort-dir-select">
                <SelectValue placeholder="Richtung" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="desc">Absteigend</SelectItem>
                <SelectItem value="asc">Aufsteigend</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-40" data-testid="payments-status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Status</SelectItem>
                <SelectItem value="pending">Offen</SelectItem>
                <SelectItem value="completed">Bezahlt</SelectItem>
                <SelectItem value="failed">Fehlgeschlagen</SelectItem>
                <SelectItem value="refunded">Rückerstattet</SelectItem>
              </SelectContent>
            </Select>
            <Select value={methodFilter} onValueChange={setMethodFilter}>
              <SelectTrigger className="w-full sm:w-40" data-testid="payments-method-filter">
                <SelectValue placeholder="Zahlungsart" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Methoden</SelectItem>
                <SelectItem value="bar">Bar</SelectItem>
                <SelectItem value="rechnung">Rechnung</SelectItem>
                <SelectItem value="kreditkarte">Kreditkarte</SelectItem>
                <SelectItem value="twint">TWINT</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {payments.length === 0 ? (
          <div className="text-center text-gray-500 py-8">Noch keine Zahlungen erfasst</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Datum</TableHead>
                  <TableHead>Kunde</TableHead>
                  <TableHead>Beschreibung</TableHead>
                  <TableHead>Rechnung</TableHead>
                  <TableHead>Zahlungsart</TableHead>
                  <TableHead className="text-right">Betrag</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayPayments.map((p) => (
                  <TableRow key={p.id} data-testid={`payment-row-${p.id}`}>
                    <TableCell data-testid={`payment-date-${p.id}`}>
                      {fmtSwissDateTime(p.created_at)}
                    </TableCell>
                    <TableCell data-testid={`payment-customer-${p.id}`}>
                      {p.customer_name ? (
                        <>
                          <div className="font-medium">{p.customer_name}</div>
                          <div className="text-xs text-gray-500">
                            {p.customer_email || p.customer_id}
                          </div>
                        </>
                      ) : (
                        p.customer_id
                      )}
                    </TableCell>
                    <TableCell className="max-w-xs truncate" title={p.description || ''} data-testid={`payment-description-${p.id}`}>
                      {p.description || '-'}
                    </TableCell>
                    <TableCell>
                      {p.invoice_id ? (
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`payment-invoice-pdf-${p.id}`}
                          onClick={async () => {
                            const token = localStorage.getItem('token');
                            try {
                              const res = await axios.get(
                                `${API}/invoices/${p.invoice_id}/pdf`,
                                {
                                  headers: { Authorization: `Bearer ${token}` },
                                  responseType: 'blob',
                                }
                              );
                              const blob = new Blob([res.data], { type: 'application/pdf' });
                              const url = window.URL.createObjectURL(blob);
                              const link = document.createElement('a');
                              link.href = url;
                              link.download = `Rechnung_${p.invoice_id}.pdf`;
                              document.body.appendChild(link);
                              link.click();
                              link.remove();
                              window.URL.revokeObjectURL(url);
                            } catch (error) {
                              console.error('Fehler beim Herunterladen der Rechnung', error);
                              toast.error('Fehler beim Herunterladen der Rechnung');
                            }
                          }}
                        >
                          PDF
                        </Button>
                      ) : (
                        <span className="text-xs text-gray-400 italic">Keine</span>
                      )}
                    </TableCell>
                    <TableCell data-testid={`payment-method-${p.id}`}>{p.payment_method}</TableCell>
                    <TableCell className="text-right font-semibold" data-testid={`payment-amount-${p.id}`}>
                      CHF {p.amount.toFixed(2)}
                    </TableCell>
                    <TableCell data-testid={`payment-status-${p.id}`}>{renderStatusBadge(p.status)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-2 justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`payment-detail-${p.id}`}
                          onClick={() => handleViewDetails(p)}
                        >
                          <Eye size={14} className="mr-1" />
                          Details
                        </Button>
                        {p.status !== 'refunded' && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`payment-refund-cash-${p.id}`}
                              onClick={() => handleRefund(p.id, 'bar')}
                            >
                              Rückerstattung (Bar)
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`payment-refund-none-${p.id}`}
                              onClick={() => handleRefund(p.id, 'keine')}
                            >
                              Keine Rückerstattung
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="flex items-center justify-between pt-4" data-testid="payments-pagination">
              <span className="text-xs text-gray-500" data-testid="payments-pagination-info">
                Seite {currentPage} von {totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  data-testid="payments-pagination-prev"
                >
                  Zurück
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  data-testid="payments-pagination-next"
                >
                  Weiter
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>

    <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
      <DialogContent className="max-w-2xl" data-testid="payment-detail-dialog">
        <DialogHeader>
          <DialogTitle data-testid="payment-detail-title">
            Zahlung {selectedPayment?.id?.slice(0, 8)}
          </DialogTitle>
        </DialogHeader>
        {selectedPayment ? (
          <div className="space-y-4" data-testid="payment-detail-body">
            <div className="grid grid-cols-2 gap-4" data-testid="payment-detail-grid">
              <div>
                <p className="text-xs text-gray-500">Kunde</p>
                <p className="font-medium" data-testid="payment-detail-customer-name">
                  {selectedPayment.customer_name || 'Unbekannt'}
                </p>
                <p className="text-xs text-gray-500" data-testid="payment-detail-customer-email">
                  {selectedPayment.customer_email || selectedPayment.customer_id || '-'}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Status</p>
                <div data-testid="payment-detail-status">{renderStatusBadge(selectedPayment.status)}</div>
              </div>
              <div>
                <p className="text-xs text-gray-500">Betrag</p>
                <p className="text-lg font-semibold" data-testid="payment-detail-amount">
                  CHF {selectedPayment.amount?.toFixed(2) || '0.00'}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Zahlungsart</p>
                <p className="font-medium" data-testid="payment-detail-method">
                  {selectedPayment.payment_method}
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4" data-testid="payment-detail-meta">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Datum</span>
                <span data-testid="payment-detail-date">
                  {fmtSwissDateTime(selectedPayment.created_at)}
                </span>
              </div>
              <div className="flex justify-between text-sm mt-2">
                <span className="text-gray-500">Beschreibung</span>
                <span className="text-right" data-testid="payment-detail-description">
                  {selectedPayment.description || '-'}
                </span>
              </div>
              <div className="flex justify-between text-sm mt-2">
                <span className="text-gray-500">Rechnung</span>
                {selectedPayment.invoice_id ? (
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid="payment-detail-invoice-download"
                    onClick={async () => {
                      const token = localStorage.getItem('token');
                      try {
                        const res = await axios.get(
                          `${API}/invoices/${selectedPayment.invoice_id}/pdf`,
                          {
                            headers: { Authorization: `Bearer ${token}` },
                            responseType: 'blob',
                          }
                        );
                        const blob = new Blob([res.data], { type: 'application/pdf' });
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = `Rechnung_${selectedPayment.invoice_id}.pdf`;
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        window.URL.revokeObjectURL(url);
                      } catch (error) {
                        toast.error('Fehler beim Herunterladen der Rechnung');
                      }
                    }}
                  >
                    PDF laden
                  </Button>
                ) : (
                  <span className="text-gray-400" data-testid="payment-detail-invoice-none">Keine</span>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-sm text-gray-500" data-testid="payment-detail-empty">Keine Details verfügbar.</div>
        )}
      </DialogContent>
    </Dialog>
    </>
  );

  return (
    <div className="space-y-6" data-testid="payments-page">
      <div>
        <h1 className="text-3xl font-bold">Zahlungen</h1>
        <p className="text-gray-600 mt-1">Verwalten Sie alle Transaktionen</p>
      </div>

      <Tabs defaultValue="payments" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="payments">Alle Zahlungen</TabsTrigger>
          <TabsTrigger value="subscription-failures">Fehlgeschlagene Abo-Zahlungen</TabsTrigger>
        </TabsList>

        <TabsContent value="payments">{paymentsContent}</TabsContent>

        <TabsContent value="subscription-failures">
          <FailedSubscriptionPaymentsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

const FailedSubscriptionPaymentsTab = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/notifications?unread_only=false&limit=100`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications(res.data || []);
    } catch (error) {
      console.error('Fehler beim Laden der Benachrichtigungen', error);
      toast.error('Fehler beim Laden der Benachrichtigungen');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const markAsRead = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/notifications/${id}/mark-read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    } catch (error) {
      console.error('Fehler beim Markieren als gelesen', error);
      toast.error('Konnte Benachrichtigung nicht als gelesen markieren');
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-gray-500">Lädt fehlgeschlagene Abo-Zahlungen...</CardContent>
      </Card>
    );
  }

  if (!notifications.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Fehlgeschlagene Abo-Zahlungen</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500">Aktuell liegen keine fehlgeschlagenen Abo-Zahlungen vor.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Fehlgeschlagene Abo-Zahlungen ({notifications.length})</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {notifications.map((n) => (
            <div
              key={n.id}
              className={`flex items-start justify-between p-3 border rounded-lg ${
                n.read ? 'bg-gray-50' : 'bg-red-50 border-red-200'
              }`}
            >
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant={n.severity === 'error' ? 'destructive' : 'outline'}>
                    {n.type === 'subscription_payment_failed'
                      ? 'Abo-Zahlung fehlgeschlagen'
                      : n.type}
                  </Badge>
                  <span className="text-xs text-gray-400">
                    {n.created_at ? fmtSwissDateTime(n.created_at) : ''}
                  </span>
                </div>
                <div className="font-medium text-sm">{n.title}</div>
                {n.message && <div className="text-sm text-gray-700 mt-1">{n.message}</div>}
                {typeof n.amount === 'number' && (
                  <div className="text-xs text-gray-500 mt-1">
                    Betrag: CHF {n.amount.toFixed(2)}
                  </div>
                )}
              </div>
              {!n.read && (
                <Button size="sm" variant="outline" onClick={() => markAsRead(n.id)}>
                  Als gelesen markieren
                </Button>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default PaymentsPage;
